import { forwardRef, useEffect, useRef, useState } from 'react';
import { SnackbarContent } from 'notistack';
import NotificationsOffRoundedIcon from '@mui/icons-material/NotificationsOffRounded';
import AutoAwesomeRoundedIcon from '@mui/icons-material/AutoAwesomeRounded';
import {
  AI_INVITE_EYEBROW,
  AI_INVITE_MUTE_LABEL,
  aiInviteOthersLine,
  INVITE_TOAST_MAX_AGE_MS,
  INVITE_TOAST_TIMEOUT_MS,
  LIVE_INVITE_TOAST_TIMEOUT_MS,
} from 'src/pages/skillBuilder/constants/multiplayer';
import type { InviteToastPropsType } from 'src/pages/skillBuilder/types/Multiplayer';
import {
  JoinButton,
  LaterButton,
  MuteButton,
  ToastActions,
  ToastAiTag,
  ToastAvatar,
  ToastBody,
  ToastCountdownBar,
  ToastEyebrow,
  ToastHint,
  ToastHintLink,
  ToastIdentity,
  ToastName,
  ToastNameRow,
  ToastRoot,
  ToastSub,
} from 'src/pages/skillBuilder/styles/multiplay/InviteToast.styles';

/**
 * "Someone challenged you", as a card. forwardRef because notistack measures
 * whatever `content` returns — a plain function component loses the transition.
 */
const InviteToast = forwardRef<HTMLDivElement, InviteToastPropsType>(
  (
    { id, payload, onJoin, onDismiss, onMute, onOpenSettings, onTimeout, ai },
    ref,
  ) => {
    const who = payload.from_user_name || 'Someone';
    const topic = payload.skill_name || payload.prompt_name || 'Quick match';
    // An open match has no subject yet — it is picked from what the two of you
    // both play, once you are both in — so it is described rather than named.
    const isOpenSkill = !!payload.is_open_skill && !payload.is_live;
    // Live rounds close to newcomers after a couple of minutes, so this offer
    // is worth taking now rather than later.
    const isLive = !!payload.is_live;
    const timeoutMs = isLive
      ? LIVE_INVITE_TOAST_TIMEOUT_MS
      : INVITE_TOAST_TIMEOUT_MS;

    // Countdown drives a shrinking bar so the user can see time running out.
    const [pct, setPct] = useState(100);
    // The handlers are rebuilt on every provider render, so without a ref any
    // other snackbar would restart this countdown at 100%.
    const timeoutRef = useRef<(seen: boolean) => void>(
      onTimeout ?? (() => onDismiss()),
    );
    timeoutRef.current = onTimeout ?? (() => onDismiss());
    // Everyone else the AI invited. Never who, and never that they joined.
    const others = ai ? Math.max(0, ai.invitedCount - 1) : 0;

    // Only time on screen runs the clock. An invite that lands while the
    // learner is in another tab used to expire there unseen; it now waits for
    // them, up to the age past which no invite can be honoured.
    useEffect(() => {
      const arrivedAt = Date.now();
      let seenMs = 0;
      let lastTick = arrivedAt;
      // Restarted on every switch, so time spent away is never counted, and
      // neither is the gap a throttled background timer leaves behind.
      const onVisibilityChange = () => {
        lastTick = Date.now();
      };
      document.addEventListener('visibilitychange', onVisibilityChange);
      const tick = setInterval(() => {
        const now = Date.now();
        if (!document.hidden) seenMs += now - lastTick;
        lastTick = now;
        const remaining = Math.max(0, 100 - (seenMs / timeoutMs) * 100);
        setPct(remaining);
        if (remaining === 0 || now - arrivedAt >= INVITE_TOAST_MAX_AGE_MS) {
          clearInterval(tick);
          timeoutRef.current(seenMs > 0);
        }
      }, 100);
      return () => {
        clearInterval(tick);
        document.removeEventListener('visibilitychange', onVisibilityChange);
      };
    }, [timeoutMs]);

    return (
      <SnackbarContent ref={ref} role="alert" id={String(id)}>
        <ToastRoot>
          <ToastEyebrow>
            {ai
              ? AI_INVITE_EYEBROW
              : isLive
                ? 'Match in progress'
                : 'Multiplayer invite'}
          </ToastEyebrow>

          <ToastIdentity>
            <ToastAvatar name={who} picture={payload.from_user_picture} />
            <ToastBody>
              <ToastNameRow>
                <ToastName title={who}>{who}</ToastName>
                {ai && (
                  <ToastAiTag>
                    <AutoAwesomeRoundedIcon />
                    AI
                  </ToastAiTag>
                )}
              </ToastNameRow>
              <ToastSub>
                {ai ? (
                  <>
                    is starting a <b>{topic}</b> game.
                    {others > 0 && ` ${aiInviteOthersLine(others)}`}
                  </>
                ) : isLive ? (
                  <>
                    is playing <b>{topic}</b> right now — jump in
                  </>
                ) : (
                  <>
                    wants to play{' '}
                    <b>{isOpenSkill ? 'a skill you both know' : topic}</b>{' '}
                    against you
                  </>
                )}
              </ToastSub>
            </ToastBody>
          </ToastIdentity>

          <ToastActions>
            {!!onMute && (
              <MuteButton
                onClick={onMute}
                startIcon={<NotificationsOffRoundedIcon />}
              >
                {ai ? AI_INVITE_MUTE_LABEL : 'Mute 5 min'}
              </MuteButton>
            )}
            <LaterButton onClick={onDismiss}>Not now</LaterButton>
            <JoinButton onClick={onJoin}>
              {isLive ? 'Jump in' : 'Join game'}
            </JoinButton>
          </ToastActions>

          <ToastHint>
            Don&apos;t want invites at all?{' '}
            <ToastHintLink onClick={onOpenSettings}>
              Turn them off in Settings
            </ToastHintLink>
          </ToastHint>

          <ToastCountdownBar style={{ width: `${pct}%` }} />
        </ToastRoot>
      </SnackbarContent>
    );
  },
);

InviteToast.displayName = 'InviteToast';

export default InviteToast;
